import { FC } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { text } from "@/theme/colors";
import { TLearningProgressFilterFormExtended } from "@/features/learning-progress/components/filter/learning-progress-filter-shared";
import { useLearningProgressFilterMasterData } from "@/features/learning-progress/components/filter/use-learning-progress-filter-master-data";
import { ComboboxMultiField } from "@/features/learning-progress/components/filter/ComboboxMultiField";
import { NumberRangeField } from "@/features/learning-progress/components/filter/NumberRangeField";

type LearningProgressFilterFormFieldsProps = {
  industryId?: number;
};

export const LearningProgressFilterFormFields: FC<LearningProgressFilterFormFieldsProps> = ({ industryId }) => {
  const { options, canFilterByOrganization } = useLearningProgressFilterMasterData(industryId);
  const { control } = useFormContext<TLearningProgressFilterFormExtended>();
  const lastLoginDateFrom = useWatch({ control, name: "lastLoginDateFrom" });
  const lastLoginDateTo = useWatch({ control, name: "lastLoginDateTo" });

  return (
    <Grid container rowSpacing={2} columnSpacing={3}>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">氏名</Typography>
          <FormField control={control} name="fullName">
            <Input placeholder="入力してください" />
          </FormField>
        </Stack>
      </Grid>
      {canFilterByOrganization && (
        <Grid item xs={6}>
          <ComboboxMultiField label="所属機関" name="rsoCompanyIds" options={options.organization} />
        </Grid>
      )}

      <Grid item xs={6}>
        <ComboboxMultiField label="国籍" name="nationalityIds" options={options.nationality} />
      </Grid>
      <Grid item xs={6}>
        <ComboboxMultiField label="試験予定" name="examIds" options={options.examSchedule} />
      </Grid>

      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">最終ログイン</Typography>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Box flex={1}>
              <FormField control={control} name="lastLoginDateFrom">
                <InputDatePicker placeholder="選択してください" maxDate={lastLoginDateTo ?? undefined} />
              </FormField>
            </Box>
            <Typography variant="cap12" color={text.secondary} sx={{ pt: "10px" }}>
              〜
            </Typography>
            <Box flex={1}>
              <FormField control={control} name="lastLoginDateTo">
                <InputDatePicker placeholder="選択してください" minDate={lastLoginDateFrom ?? undefined} />
              </FormField>
            </Box>
          </Stack>
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <NumberRangeField label="学習時間（合計）" fromKey="learningTimeFrom" toKey="learningTimeTo" />
      </Grid>
      <Grid item xs={6}>
        <NumberRangeField label="学習時間（今週）" fromKey="thisWeekLearningTimeFrom" toKey="thisWeekLearningTimeTo" />
      </Grid>

      <Grid item xs={6}>
        <ComboboxMultiField label="学習進捗" name="curriculumProgresses" options={options.progress} />
      </Grid>
      <Grid item xs={6}>
        <NumberRangeField label="覚えた単語数" fromKey="learnedVocabCountFrom" toKey="learnedVocabCountTo" />
      </Grid>

      <Grid item xs={6}>
        <NumberRangeField label="模試実施回数" fromKey="mockExamCountFrom" toKey="mockExamCountTo" />
      </Grid>
      <Grid item xs={6}>
        <NumberRangeField label="直近模試結果" fromKey="latestMockExamScoreFrom" toKey="latestMockExamScoreTo" />
      </Grid>

      <Grid item xs={6}>
        <NumberRangeField label="前回本試験点数" fromKey="lastExamScoreFrom" toKey="lastExamScoreTo" />
      </Grid>
      <Grid item xs={6}>
        <ComboboxMultiField label="日本語能力" name="japaneseLanguageLevels" options={options.japaneseLevel} />
      </Grid>
    </Grid>
  );
};
